/**
 * POKEMON GAME LOGIC
 * ==================
 * This file exports the unique logic and components for the Pokemon game.
 * All shared configuration data has been moved to the unified config.
 */

import PokemonHint from '../../components/PokemonHint/PokemonHint';
import PokemonDirectory from '../../components/PokemonDirectory/PokemonDirectory';
import PokemonHowToPlay from '../../components/PokemonHowToPlay/PokemonHowToPlay';

const comparePokemon = (guessed, target) => {
  const results = {};
  
  // Handle null/undefined inputs
  if (!guessed || !target) {
    return {
      type1: 'incorrect',
      type2: 'incorrect',
      color: 'incorrect',
      evolution_stage: 'incorrect',
      evolution_method: 'incorrect',
      height_m: 'incorrect',
      weight_kg: 'incorrect'
    };
  }
  
  // Type comparisons
  results.type1 = guessed.type1 === target.type1 ? 'correct' : 'incorrect';
  results.type2 = guessed.type2 === target.type2 ? 'correct' : 'incorrect';
  
  // Color comparison - check for partial matches
  const guessedColors = (guessed.color || '').toLowerCase().split(',').map(c => c.trim());
  const targetColors = (target.color || '').toLowerCase().split(',').map(c => c.trim());
  
  if (guessed.color === target.color) {
    results.color = 'correct';
  } else {
    const hasCommonColor = guessedColors.some(gc => targetColors.includes(gc));
    results.color = hasCommonColor ? 'partial' : 'incorrect';
  }
  
  // Evolution stage
  results.evolution_stage = guessed.evolution_stage === target.evolution_stage ? 'correct' : 'incorrect';
  
  // Evolution method
  results.evolution_method = guessed.evolution_method === target.evolution_method ? 'correct' : 'incorrect';
  
  // Height comparison with directional feedback
  if (guessed.height_m === target.height_m) {
    results.height_m = 'correct';
  } else {
    const heightDiff = Math.abs(guessed.height_m - target.height_m);
    if (heightDiff <= 0.3) {
      results.height_m = guessed.height_m < target.height_m ? 'partial-up' : 'partial-down';
    } else {
      results.height_m = guessed.height_m < target.height_m ? 'partial-up' : 'partial-down';
    }
  }
  
  // Weight comparison with directional feedback
  if (guessed.weight_kg === target.weight_kg) {
    results.weight_kg = 'correct';
  } else {
    const weightDiff = Math.abs(guessed.weight_kg - target.weight_kg);
    if (weightDiff <= 10) {
      results.weight_kg = guessed.weight_kg < target.weight_kg ? 'partial-up' : 'partial-down';
    } else {
      results.weight_kg = guessed.weight_kg < target.weight_kg ? 'partial-up' : 'partial-down';
    }
  }

  return results;
};

const getDisplayValue = (pokemon, key) => {
  let val = pokemon[key];
  
  if (val == null || String(val).trim() === '') return 'None';
  
  if (key === 'height_m') {
    return `${val}m`;
  }
  
  if (key === 'weight_kg') {
    return `${val}kg`;
  }
  
  if (key === 'evolution_stage') {
    return `Stage ${val}`;
  }
  
  return String(val);
};

const pokemonLogic = {
  // Categories for the game comparison
  categories: [
    { key: 'type1', label: 'Type 1' },
    { key: 'type2', label: 'Type 2' },
    { key: 'color', label: 'Color' },
    { key: 'evolution_stage', label: 'Evolution Stage' },
    { key: 'evolution_method', label: 'Evolution Method' },
    { key: 'height_m', label: 'Height' },
    { key: 'weight_kg', label: 'Weight' }
  ],
  compareFunction: comparePokemon,
  getDisplayValue,
  
  HintComponent: PokemonHint,
  DirectoryComponent: PokemonDirectory,
  HowToPlayComponent: PokemonHowToPlay,
  
  renderVictoryDetails: (pokemon) => (
    <>
      {pokemon.hint && (
        <div className="pokemon__victory-description">
          <strong>Description:</strong> {pokemon.hint}
        </div>
      )}
    </>
  )
};

export default pokemonLogic;